import React, { useState, useEffect } from 'react';
import { Search, User, Crown, Star, Eye, Download, Filter } from 'lucide-react';
import { customerAPI, exportAPI } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerTransactions, setCustomerTransactions] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, [tierFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchCustomers();
    }, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  const fetchCustomers = async () => {
    try {
      const params = { search, limit: 100 };
      if (tierFilter !== 'all') params.tier = tierFilter;
      const response = await customerAPI.getAll(params);
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewCustomer = async (customer) => {
    setSelectedCustomer(customer);
    try {
      const response = await customerAPI.getTransactions(customer.id, 20);
      setCustomerTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportAPI.customers();
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `salon-o-customers-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      toast.success(`Exported ${response.data.count} customers`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const getTierColor = (tier) => ({
    Bronze: 'tier-bronze',
    Silver: 'tier-silver',
    Gold: 'tier-gold',
    Platinum: 'tier-platinum',
  })[tier] || 'tier-bronze';

  return (
    <div className="space-y-6 pt-12 lg:pt-0 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#4A154B]">Customers</h1>
          <p className="text-gray-600">Manage your customer base</p>
        </div>
        <Button onClick={handleExport} disabled={exporting} variant="outline" data-testid="export-btn">
          <Download className="w-4 h-4 mr-2" />
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or phone..."
            className="pl-12"
            data-testid="customer-search-input"
          />
        </div>
        <Select value={tierFilter} onValueChange={setTierFilter}>
          <SelectTrigger className="w-40" data-testid="tier-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            <SelectItem value="Bronze">Bronze</SelectItem>
            <SelectItem value="Silver">Silver</SelectItem>
            <SelectItem value="Gold">Gold</SelectItem>
            <SelectItem value="Platinum">Platinum</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4,5].map(i => <div key={i} className="skeleton h-16 rounded-xl" />)}
        </div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F5E6D3]/50">
                <tr>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-600">Customer</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-gray-600">Contact</th>
                  <th className="text-center py-4 px-4 text-sm font-medium text-gray-600">Tier</th>
                  <th className="text-right py-4 px-4 text-sm font-medium text-gray-600">Current Pts</th>
                  <th className="text-right py-4 px-4 text-sm font-medium text-gray-600">Lifetime</th>
                  <th className="text-center py-4 px-4 text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id} className="border-b border-[#F5E6D3]/50 hover:bg-[#F5E6D3]/20">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#F5E6D3] flex items-center justify-center">
                          <User className="w-5 h-5 text-[#C9A689]" />
                        </div>
                        <span className="font-medium text-[#4A154B]">{customer.name || 'Guest'}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-sm text-gray-600">{customer.email}</p>
                      {customer.phone && <p className="text-xs text-gray-400">{customer.phone}</p>}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getTierColor(customer.tier)}`}>
                        {customer.tier}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right font-medium text-[#4A154B]">
                      {customer.current_points}
                    </td>
                    <td className="py-4 px-4 text-right text-gray-600">
                      {customer.lifetime_points}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewCustomer(customer)}
                        data-testid={`view-customer-${customer.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {customers.length === 0 && (
            <div className="text-center py-12">
              <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No customers found</p>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#4A154B]">Customer Details</DialogTitle>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-[#F5E6D3] flex items-center justify-center">
                  <User className="w-8 h-8 text-[#C9A689]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-xl text-[#4A154B]">{selectedCustomer.name || 'Guest'}</h3>
                  <p className="text-gray-500">{selectedCustomer.email}</p>
                  {selectedCustomer.phone && <p className="text-gray-500 text-sm">{selectedCustomer.phone}</p>}
                  <div className="flex items-center gap-3 mt-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getTierColor(selectedCustomer.tier)}`}>
                      <Crown className="w-4 h-4 inline mr-1" />{selectedCustomer.tier}
                    </span>
                    <span className="text-sm text-gray-500">
                      Member since {format(new Date(selectedCustomer.created_at), 'MMM d, yyyy')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#F5E6D3] rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-600">Current Points</p>
                  <p className="font-serif text-2xl text-[#4A154B]">{selectedCustomer.current_points}</p>
                </div>
                <div className="bg-[#F5E6D3] rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-600">Lifetime Points</p>
                  <p className="font-serif text-2xl text-[#4A154B]">{selectedCustomer.lifetime_points}</p>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-[#4A154B] mb-3">Transaction History</h4>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {customerTransactions.length > 0 ? customerTransactions.map((t) => (
                    <div key={t.id} className="flex items-center justify-between py-2 px-3 bg-[#F5E6D3]/30 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-[#4A154B]">
                          {t.service_name || t.reward_name || 'Transaction'}
                        </p>
                        <p className="text-xs text-gray-500">{format(new Date(t.created_at), 'MMM d, yyyy h:mm a')}</p>
                      </div>
                      <div className="text-right">
                        <span className={`font-semibold ${t.type === 'earned' ? 'text-[#8FBC8F]' : 'text-[#C74545]'}`}>
                          {t.type === 'earned' ? '+' : ''}{t.points}
                        </span>
                        {t.amount && <p className="text-xs text-gray-500">${t.amount.toFixed(2)}</p>}
                      </div>
                    </div>
                  )) : (
                    <p className="text-gray-500 text-center py-4">No transactions</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomers;
